import { useRef, useState } from 'react';
import { Stethoscope, Camera, Paperclip, ShieldAlert, FileCheck, Ban, ArrowLeft, ScanLine, Smartphone } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
// The legacy, untyped upload helper in src/api.js — reused deliberately rather
// than reimplemented: it is the one place that knows how to send a multipart
// body with the auth and branch headers attached. A narrow local type is
// declared at the call site, which is the convention for consuming that module
// from TypeScript (see CLAUDE.md's note on src/api.js).
import { uploadPatientDocument } from '@/api';
// The SAME scan-to-upload flow the Patient Records Documents tab and the
// Pre-Consult stage already use — one QR/session/polling implementation, so
// a phone that can upload to one of them can upload to all three.
import MobileUploadQRModal, { type MobileSessionStatus } from '@/components/MobileUploadQRModal';
import { useCaptureOutpatientPrescription } from '../useOutpatient';
import { shortDate } from './ui';
import type { CounterCustomer, OutpatientPrescription } from '../types';

/**
 * Step 2 of the counter: the outside doctor's prescription.
 *
 * This is provenance for a piece of paper somebody else wrote — who signed it,
 * when, and optionally a photo of it. It is deliberately NOT a `prescriptions`
 * row: that table models a prescription written by one of OUR doctors, with
 * structured items and a dispensed/remaining calculation that only makes sense
 * for our own prescribing.
 *
 * The photo goes through the ordinary patient-documents upload endpoint, so a
 * photographed slip is stored, OCR'd and viewable through exactly the same
 * machinery as every other patient document — the counter stores no files of
 * its own.
 *
 * Skipping is allowed for an OTC-only sale but never silently: a reason is
 * required, and the SALE (not this form) refuses outright if the cart holds a
 * Schedule H/H1/X medicine. That check lives server-side, where it cannot be
 * clicked past — this screen only explains it early, so the operator finds out
 * before the customer has been told a price.
 */

/** The two ways a counter sale can be justified. Presented as a choice, not a checkbox. */
function ModeToggle({
  skip, onChange, lockedToPrescription,
}: {
  skip: boolean;
  onChange: (skip: boolean) => void;
  lockedToPrescription: boolean;
}) {
  const opt =
    'flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none ' +
    'focus-visible:ring-[3px] focus-visible:ring-teal/25 disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <div className="mb-3.5 flex flex-col gap-2 sm:flex-row">
      <button type="button" className={cn(opt, !skip ? 'border-teal bg-accent' : 'border-border hover:bg-muted')} onClick={() => onChange(false)}>
        <span className="flex items-center gap-1.5 text-[12.5px] font-semibold">
          <Stethoscope size={14} /> Has a prescription
        </span>
        <span className="mt-0.5 block text-[11px] text-ink-muted">From any doctor, ours or outside</span>
      </button>
      <button
        type="button"
        disabled={lockedToPrescription}
        className={cn(opt, skip ? 'border-teal bg-accent' : 'border-border hover:bg-muted')}
        onClick={() => onChange(true)}
        title={lockedToPrescription ? 'Unavailable — the cart contains a Schedule H/H1/X medicine' : undefined}
      >
        <span className="flex items-center gap-1.5 text-[12.5px] font-semibold">
          <Ban size={14} /> Over the counter
        </span>
        <span className="mt-0.5 block text-[11px] text-ink-muted">OTC only — a reason is required</span>
      </button>
    </div>
  );
}

export default function PrescriptionPanel({
  customer,
  prescription,
  onCaptured,
  onClear,
  cartHasScheduled,
}: {
  customer: CounterCustomer;
  prescription: OutpatientPrescription | null;
  onCaptured: (rx: OutpatientPrescription) => void;
  onClear: () => void;
  /** True when any cart line is Schedule H/H1/X — skipping is then impossible. */
  cartHasScheduled: boolean;
}) {
  const [skip, setSkip] = useState(false);
  const [draft, setDraft] = useState({
    prescriber_name: '',
    prescriber_reg_no: '',
    prescriber_hospital: '',
    prescribed_on: '',
    notes: '',
    skip_reason: '',
  });
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);

  const capture = useCaptureOutpatientPrescription();

  const upload = async (file: File) => {
    setUploading(true);
    setUploadError('');
    try {
      const doc = (await uploadPatientDocument(customer.uhid, file, 'Prescription')) as { id: number };
      setDocumentId(doc.id);
      setUploadName(file.name);
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : 'Could not upload that photo');
    }
    setUploading(false);
  };

  /**
   * A slip photographed on a phone has landed.
   *
   * The counter needs the document's ID, not just a refresh signal, because
   * the prescription record it is about to save points at that exact row —
   * which is why the session-status endpoint returns the documents it
   * received rather than the counter reading the whole patient file. Newest
   * first, so [0] is the page that was just taken. The modal stays open: a
   * prescription is often two or three pages, and each one replaces this
   * link, so the last page scanned is the one attached (and every page is on
   * the patient's record regardless).
   */
  const onScanReceived = (status: MobileSessionStatus) => {
    const latest = status.documents?.[0];
    if (!latest) return;
    setDocumentId(latest.id);
    setUploadName(latest.original_name);
    setUploadError('');
  };

  const submit = () => {
    capture.mutate(
      {
        patient_id: customer.id,
        ...(skip
          ? { skipped: true, skip_reason: draft.skip_reason.trim() }
          : {
              prescriber_name: draft.prescriber_name.trim(),
              prescriber_reg_no: draft.prescriber_reg_no.trim() || undefined,
              prescriber_hospital: draft.prescriber_hospital.trim() || undefined,
              prescribed_on: draft.prescribed_on || undefined,
              notes: draft.notes.trim() || undefined,
              document_id: documentId ?? undefined,
            }),
      },
      { onSuccess: onCaptured }
    );
  };

  const error = capture.error instanceof ApiError ? capture.error.message : null;
  const canSubmit = skip ? draft.skip_reason.trim().length > 0 : draft.prescriber_name.trim().length > 0;

  // ── Captured ───────────────────────────────────────────────────
  if (prescription) {
    const blocked = prescription.skipped && cartHasScheduled;
    return (
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'grid h-9 w-9 shrink-0 place-content-center rounded-full',
              blocked ? 'bg-danger-soft text-danger-ink' : prescription.skipped ? 'bg-warn-soft text-warn-ink' : 'bg-success-soft text-success-ink'
            )}
          >
            {prescription.skipped ? <Ban size={16} /> : <FileCheck size={16} />}
          </span>
          <div className="min-w-0 flex-1">
            {prescription.skipped ? (
              <>
                <div className="text-[13px] font-semibold">Over-the-counter sale</div>
                <div className="mt-0.5 text-[11.5px] text-ink-muted">Reason: {prescription.skip_reason}</div>
              </>
            ) : (
              <>
                <div className="truncate text-[13px] font-semibold">Dr. {prescription.prescriber_name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11.5px] text-ink-muted">
                  {prescription.prescriber_hospital && <span>{prescription.prescriber_hospital}</span>}
                  {prescription.prescribed_on && <span>· {shortDate(prescription.prescribed_on)}</span>}
                  {prescription.prescriber_reg_no && <span>· Reg {prescription.prescriber_reg_no}</span>}
                </div>
                {prescription.document_id != null && (
                  <Badge variant="info" className="mt-1.5 inline-flex items-center gap-1">
                    <Paperclip size={10} /> Photo attached
                  </Badge>
                )}
              </>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClear} className="shrink-0">
            <ArrowLeft size={13} /> Change
          </Button>
        </div>

        {/* The cart can change AFTER the prescription was captured, so this
            warning has to live here rather than only at capture time. */}
        {blocked && (
          <div className="mt-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
            <ShieldAlert size={15} className="mt-px shrink-0" />
            <p className="leading-relaxed">
              The cart now contains a <strong>Schedule H/H1/X</strong> medicine, which cannot be sold
              over the counter. Add the prescriber's details, or remove that medicine.
            </p>
          </div>
        )}
      </Card>
    );
  }

  // ── Capturing ──────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader>
        <CardTitle><Stethoscope size={15} /> Prescription</CardTitle>
      </CardHeader>

      {error && <div className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <ModeToggle skip={skip} onChange={setSkip} lockedToPrescription={cartHasScheduled} />

      {cartHasScheduled && (
        <div className="mb-3.5 flex gap-2.5 rounded-lg bg-warn-soft px-3 py-2.5 text-[11.5px] text-caution-ink">
          <ShieldAlert size={15} className="mt-px shrink-0" />
          <p className="leading-relaxed">
            The cart contains a <strong>Schedule H/H1/X</strong> medicine. Under the Drugs and
            Cosmetics Rules these cannot be sold without a prescription, so an over-the-counter sale
            is unavailable.
          </p>
        </div>
      )}

      {skip ? (
        <FormField label="Why is there no prescription?" required>
          <Input
            autoFocus
            value={draft.skip_reason}
            placeholder="e.g. OTC purchase — paracetamol, antacid"
            onChange={(e) => setDraft((d) => ({ ...d, skip_reason: e.target.value }))}
          />
        </FormField>
      ) : (
        <div className="space-y-3">
          <div className="grid2">
            <FormField label="Prescriber's name" required>
              <Input
                autoFocus
                value={draft.prescriber_name}
                placeholder="Dr. …"
                onChange={(e) => setDraft((d) => ({ ...d, prescriber_name: e.target.value }))}
              />
            </FormField>
            <FormField label="Registration no.">
              <Input value={draft.prescriber_reg_no} onChange={(e) => setDraft((d) => ({ ...d, prescriber_reg_no: e.target.value }))} />
            </FormField>
          </div>
          <div className="grid2">
            <FormField label="Hospital / clinic">
              <Input value={draft.prescriber_hospital} onChange={(e) => setDraft((d) => ({ ...d, prescriber_hospital: e.target.value }))} />
            </FormField>
            <FormField label="Prescribed on">
              <Input type="date" value={draft.prescribed_on} onChange={(e) => setDraft((d) => ({ ...d, prescribed_on: e.target.value }))} />
            </FormField>
          </div>
          <FormField label="Notes">
            <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
          </FormField>

          <div>
            <div className="lbl">Photo of the slip</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
            />
            {uploadName ? (
              <div className="flex items-center gap-2 rounded-lg border border-success-soft bg-success-soft px-3 py-2 text-[12px] text-success-ink">
                <Paperclip size={13} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{uploadName}</span>
                <button
                  type="button"
                  className="shrink-0 text-[11px] underline-offset-2 hover:underline"
                  onClick={() => { setUploadName(''); setDocumentId(null); }}
                >
                  Remove
                </button>
              </div>
            ) : (
              // Two ways in, side by side rather than one behind the other.
              // The phone is usually the RIGHT one at a counter — the slip is
              // paper in the customer's hand and there is no scanner at the
              // till — but a desk that has already scanned the page needs the
              // file picker, so neither is buried in a menu.
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setScanning(true)}
                  className={
                    'flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border ' +
                    'px-3 py-3 text-[12px] text-ink-muted transition-colors hover:border-teal hover:bg-accent'
                  }
                >
                  <ScanLine size={17} />
                  <span className="font-medium">Scan with a phone</span>
                  <span className="text-[10.5px] text-ink-muted/75">Show a QR code</span>
                </button>
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className={
                    'flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border ' +
                    'px-3 py-3 text-[12px] text-ink-muted transition-colors hover:border-teal hover:bg-accent ' +
                    'disabled:pointer-events-none disabled:opacity-60'
                  }
                >
                  <Camera size={17} />
                  <span className="font-medium">{uploading ? 'Uploading…' : 'Choose a file'}</span>
                  <span className="text-[10.5px] text-ink-muted/75">From this computer</span>
                </button>
              </div>
            )}
            {uploadError && <div className="mt-1.5 text-[11px] text-danger">{uploadError}</div>}
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-ink-muted">
              <Smartphone size={11} className="shrink-0" />
              Optional. Either way it is stored on the patient's record like any other document.
            </p>
          </div>
        </div>
      )}

      <Button
        full
        className="mt-4"
        disabled={!canSubmit || capture.isPending}
        loading={capture.isPending}
        onClick={submit}
      >
        {skip ? 'Confirm over-the-counter sale' : 'Save prescription details'}
      </Button>

      {scanning && (
        <MobileUploadQRModal
          uhid={customer.uhid}
          category="Prescription"
          title="Scan the prescription"
          hint="Ask the customer to scan this with their phone's camera and photograph the prescription. It attaches to this sale automatically."
          onReceived={onScanReceived}
          onClose={() => setScanning(false)}
        />
      )}
    </Card>
  );
}
